import React, { useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import { X } from 'lucide-react';
import styles from './TopTraders.module.sass';

const MetricCard = ({ label, value, subValue }) => (
  <div className={styles.metric}>
    <div className={styles.metricContent}>
      <div className={styles.metricStats}>
        <div className={styles.metricLabel}>{label}</div>
        <div className={styles.metricValue}>
          {value}
          {subValue && <span className={styles.numericalScore}>{subValue}</span>}
        </div>
      </div>
    </div>
  </div>
);

const TooltipContent = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className={styles.tooltip}>
        <p className={styles.label}>{label}</p>
        <p>Traders: {payload[0].value}</p>
        {payload[0].payload.totalProfit && (
          <p>Total Profit: ${payload[0].payload.totalProfit.toLocaleString()}</p>
        )}
      </div>
    );
  }
  return null;
};

const ModalContent = ({ tradersArray, onClose }) => {
  const stats = useMemo(() => {
    if (!tradersArray.length) {
      return {
        avgProfit: '0.00',
        totalTrades: 0,
        avgTradesPerTrader: 0,
        totalInvestment: 0,
        totalProfit: 0
      };
    }

    const profits = tradersArray.map(item => ({
      trades: item.count_of_trades,
      investment: parseFloat(item.total_usd_invested || '0'),
      profit: parseFloat(item.realized_profit_usd || '0'),
      profitPercent: parseFloat(item.realized_profit_percentage?.toString() || '0')
    }));

    return {
      avgProfit: (profits.reduce((acc, curr) => acc + curr.profitPercent, 0) / profits.length).toFixed(2),
      totalTrades: profits.reduce((acc, curr) => acc + curr.trades, 0),
      avgTradesPerTrader: (profits.reduce((acc, curr) => acc + curr.trades, 0) / profits.length).toFixed(1),
      totalInvestment: profits.reduce((acc, curr) => acc + curr.investment, 0),
      totalProfit: profits.reduce((acc, curr) => acc + curr.profit, 0)
    };
  }, [tradersArray]);

  const histogramData = useMemo(() => {
    const profits = tradersArray
      .map(trader => parseFloat(trader.realized_profit_percentage?.toString() || '0'))
      .filter(profit => !isNaN(profit));

    if (profits.length === 0) return [];

    const min = Math.min(...profits);
    const max = Math.max(...profits);
    
    const binCount = 20;
    const binSize = (max - min) / binCount;
    
    const bins = Array.from({ length: binCount }, (_, i) => ({
      binStart: min + (i * binSize),
      binEnd: min + ((i + 1) * binSize),
      count: 0,
      totalProfit: 0,
    }));

    tradersArray.forEach(trader => {
      const profit = parseFloat(trader.realized_profit_percentage?.toString() || '0');
      const profitUSD = parseFloat(trader.realized_profit_usd || '0');
      
      if (isNaN(profit) || isNaN(profitUSD)) return;

      const binIndex = Math.min(
        Math.floor((profit - min) / binSize),
        binCount - 1
      );
      
      if (binIndex >= 0 && binIndex < bins.length) {
        bins[binIndex].count++;
        bins[binIndex].totalProfit += profitUSD;
      }
    });

    return bins
      .map(bin => ({
        range: `${bin.binStart.toFixed(0)}%-${bin.binEnd.toFixed(0)}%`,
        count: bin.count,
        totalProfit: bin.totalProfit
      }))
      .filter(bin => bin.count > 0);
  }, [tradersArray]);

  if (!tradersArray.length) {
    return <div className={styles.noData}>No trader data available</div>;
  }

  return (
    <div className={styles.fullScreenModal}>
      <h2>
        Top Traders Analysis
        <button onClick={onClose}><X size={20} /></button>
      </h2>
      
      <div className={styles.contentWrapper}>
        <div className={styles.container}>
          {/* Metrics Row */}
          <div className={styles.metricRow}>
            <MetricCard 
              label="Total Trades"
              value={stats.totalTrades.toLocaleString()}
              subValue={`${stats.avgTradesPerTrader} avg/trader`}
            />
            <MetricCard 
              label="Investment Volume"
              value={`$${stats.totalInvestment.toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
            />
            <MetricCard 
              label="Total Profit"
              value={`$${stats.totalProfit.toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
              subValue={`${stats.avgProfit}% avg`}
            />
          </div>

          {/* Histogram */}
          <div className={styles.chartContainer} style={{ flex: 1, minHeight: 0 }}>
            <h3>Profit Distribution</h3>
            <div style={{ height: 'calc(100% - 40px)' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={histogramData} barGap={0}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(115,246,255,0.1)" />
                  <XAxis
                    dataKey="range"
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    interval={1}
                    tick={{ fill: 'rgba(115,246,255,0.6)', fontSize: 10 }}
                  />
                  <YAxis tick={{ fill: 'rgba(115,246,255,0.6)', fontSize: 10 }} />
                  <Tooltip content={<TooltipContent />} />
                  <Bar
                    dataKey="count"
                    fill="rgba(115,246,255,0.6)"
                    name="Number of Traders"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const TopTradersModal = ({ tradersArray, onClose }) => {
  return createPortal(
    <ModalContent tradersArray={tradersArray} onClose={onClose} />,
    document.body
  );
};

export default TopTradersModal;